import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  StatusBadge,
  EmptyState,
  EmployeeSearchInput,
  useConfirm,
} from "./shared/components";
import {
  getUserCapabilities,
  getVigilanceInbox,
  getVigilanceProcessed,
  getVigilanceDelegatedByMe,
  listVigilanceSubNodals,
  createVigilanceSubNodal,
  deleteVigilanceSubNodal,
  delegateVigilanceCase,
  revokeVigilanceDelegation,
  submitVigilanceAction,
  submitVigilanceSubAction,
  formatDateLong,
  VigilanceCaseRecord,
  VigilanceDecision,
  VigilanceSubNodal,
} from "./services/eSeparation.service";

const statusMeta = (s: string) => {
  const u = String(s || "").toUpperCase();
  if (u === "CLEARED") return { label: "Cleared", bg: "#E4F7E8", color: "#049123" };
  if (u === "HOLD") return { label: "Hold", bg: "#F3E5F3", color: "#800080" };
  if (u === "OBJECTION") return { label: "Objection", bg: "#FDE5E5", color: "#C0282C" };
  if (u === "DELEGATED") return { label: "With Sub-Nodal", bg: "#E5F0FA", color: "#185FA5" };
  if (u === "RETURNED") return { label: "Returned by Sub-Nodal", bg: "#E5F0FA", color: "#185FA5" };
  return { label: "Pending", bg: "#FC9D4520", color: "#BA7517" };
};

const decisionLabel = (s?: string | null) => statusMeta(s || "").label;

const DECISION_OPTIONS = [
  {
    key: "CLEARED",
    label: "Cleared",
    icon: "ti-circle-check",
    color: "#049123",
    desc: "No vigilance pending / Disciplinary cleared",
  },
  {
    key: "HOLD",
    label: "Hold",
    icon: "ti-player-pause",
    color: "#800080",
    desc: "Case under active inquiry / Hold clearance",
  },
  {
    key: "OBJECTION",
    label: "Objection",
    icon: "ti-alert-triangle",
    color: "#C0282C",
    desc: "Vigilance objection / Dues / Case registered",
  },
] as const;

const padEmpNo = (v?: string | null): string => {
  const t = (v ?? "").trim();
  return t ? t.padStart(8, "0") : "";
};

const VigilanceClearancePage: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const [tab, setTab] = useState<"Pending" | "Processed">("Pending");
  const [pending, setPending] = useState<VigilanceCaseRecord[]>([]);
  const [processed, setProcessed] = useState<VigilanceCaseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<VigilanceCaseRecord | null>(null);

  const [status, setStatus] = useState<VigilanceDecision | "">("");
  const [remarks, setRemarks] = useState("");
  const [pendingCaseRef, setPendingCaseRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Sub-nodal delegation. The vigilance nodal owns the sub-nodal registry and can hand a
  // pending case to one of them; the sub-nodal records findings and the case comes back to
  // the nodal, who sends the final outcome to HR.
  const [isNodal, setIsNodal] = useState(false);
  const [isSubNodal, setIsSubNodal] = useState(false);
  const [subNodals, setSubNodals] = useState<VigilanceSubNodal[]>([]);
  const [delegated, setDelegated] = useState<VigilanceCaseRecord[]>([]);
  const [newSubQuery, setNewSubQuery] = useState("");
  const [newSubEmpId, setNewSubEmpId] = useState("");
  const [savingSub, setSavingSub] = useState(false);
  const [delegateTo, setDelegateTo] = useState("");
  const [delegating, setDelegating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [inbox, done, caps] = await Promise.all([
        getVigilanceInbox(),
        getVigilanceProcessed(),
        getUserCapabilities(),
      ]);
      setPending(inbox.data || []);
      setProcessed(done.data || []);
      const nodal = !!caps.roles.is_vigilance_officer;
      setIsNodal(nodal);
      setIsSubNodal(!nodal && !!caps.roles.is_vigilance_sub_nodal);
      if (nodal) {
        const [subs, byMe] = await Promise.all([
          listVigilanceSubNodals(),
          getVigilanceDelegatedByMe(),
        ]);
        setSubNodals(subs.data || []);
        setDelegated(byMe.data || []);
      } else {
        setSubNodals([]);
        setDelegated([]);
      }
    } catch (err: any) {
      console.error("Failed to load vigilance cases", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCase = (c: VigilanceCaseRecord) => {
    setSelected(c);
    // A case a sub-nodal has returned starts from what they recorded, so the nodal reviews
    // and confirms (or changes) it rather than retyping.
    if (c.status === "RETURNED") {
      setStatus((c.sub_nodal_status as VigilanceDecision) || "");
      setRemarks(c.sub_nodal_remarks || "");
      setPendingCaseRef(c.sub_nodal_pending_case_ref || "");
    } else {
      setStatus("");
      setRemarks("");
      setPendingCaseRef("");
    }
    const first = subNodals[0]?.sub_nodal_emp_id || "";
    setDelegateTo(first);
  };

  const closeCase = () => setSelected(null);

  const handleSubmit = async () => {
    if (!selected) return;
    if (!status) return toast.warning("Please select a clearance status.");
    if ((status === "HOLD" || status === "OBJECTION") && !remarks.trim())
      return toast.warning("Please provide remarks for Hold / Objection.");

    const who = selected.emp_name || selected.emp_no || selected.request_id;
    const asSubNodal = isSubNodal && selected.status === "DELEGATED";
    const message = asSubNodal
      ? `Submit your findings ("${statusMeta(status).label}") for ${who} to the vigilance nodal? They will review it and send it to HR.`
      : `Mark this case as "${statusMeta(status).label}" for ${who} and send it to HR?`;
    if (
      !(await confirm(message, {
        title: asSubNodal ? "Confirm Submission to Nodal" : "Confirm Clearance Submission",
        confirmLabel: asSubNodal ? "Submit to Nodal" : "Send to HR",
        tone: status === "CLEARED" ? "success" : "warning",
      }))
    )
      return;

    setSubmitting(true);
    const payload = {
      case_id: selected.id,
      status,
      remarks,
      pending_case_ref: pendingCaseRef,
    };
    const res = asSubNodal
      ? await submitVigilanceSubAction(payload)
      : await submitVigilanceAction(payload);
    setSubmitting(false);
    if (res.success) {
      toast.success(
        asSubNodal
          ? "Findings submitted to the vigilance nodal."
          : "Vigilance clearance sent to HR."
      );
      setSelected(null);
      load();
    } else {
      toast.error(res.message || "Failed to submit clearance.");
    }
  };

  const addSubNodal = async () => {
    if (!newSubEmpId.trim()) return toast.warning("Search and select the employee to add.");
    setSavingSub(true);
    const res = await createVigilanceSubNodal(padEmpNo(newSubEmpId));
    setSavingSub(false);
    if (res.success) {
      toast.success("Sub-nodal added successfully.");
      setNewSubQuery("");
      setNewSubEmpId("");
      load();
    } else {
      toast.error(res.message || "Failed to add sub-nodal.");
    }
  };

  const removeSubNodal = async (sub: VigilanceSubNodal) => {
    if (
      !(await confirm(`Remove ${sub.sub_nodal_name || sub.sub_nodal_emp_id} as a sub-nodal?`, {
        title: "Remove Sub-Nodal",
        confirmLabel: "Remove",
        tone: "danger",
      }))
    )
      return;
    const res = await deleteVigilanceSubNodal(sub.id);
    if (res.success) {
      toast.success("Sub-nodal removed.");
      load();
    } else {
      toast.error(res.message || "Failed to remove sub-nodal.");
    }
  };

  const doDelegate = async () => {
    if (!selected || !delegateTo) return;
    const sub = subNodals.find((x) => x.sub_nodal_emp_id === delegateTo);
    if (
      !(await confirm(
        `Delegate this case to ${sub?.sub_nodal_name || delegateTo}? It will leave your desk until they submit their findings.`,
        { title: "Delegate to Sub-Nodal", confirmLabel: "Delegate", tone: "primary" }
      ))
    )
      return;
    setDelegating(true);
    const res = await delegateVigilanceCase({ case_id: selected.id, sub_nodal_emp_id: delegateTo });
    setDelegating(false);
    if (res.success) {
      toast.success("Vigilance case delegated to the sub-nodal.");
      setSelected(null);
      load();
    } else {
      toast.error(res.message || "Failed to delegate.");
    }
  };

  const doRevoke = async (c: VigilanceCaseRecord) => {
    if (
      !(await confirm(
        `Take this case back from ${c.delegated_to_name || c.delegated_to_emp_id}?`,
        { title: "Revoke Delegation", confirmLabel: "Revoke", tone: "danger" }
      ))
    )
      return;
    const res = await revokeVigilanceDelegation(c.id);
    if (res.success) {
      toast.success("Delegation revoked.");
      load();
    } else {
      toast.error(res.message || "Failed to revoke delegation.");
    }
  };

  const rows = tab === "Pending" ? pending : processed;

  return (
    <div className="page-wrapper">
      {confirmDialog}
      <div className="content pb-5">
        {/* PAGE HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto">
            <h3 className="page-title fw-bold text-dark mb-1">
              Vigilance Clearance
            </h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.hub} className="text-decoration-none text-muted">
                    E-Separation
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.Esepration.noDues} className="text-decoration-none text-muted">
                    No Dues
                  </Link>
                </li>
                <li className="breadcrumb-item active text-primary fw-medium" aria-current="page">
                  Vigilance Clearance
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <span className="badge bg-purple-light text-purple px-3 py-2 fw-semibold fs-13 d-inline-flex align-items-center gap-1.5">
              <i className="ti ti-shield-check fs-16"></i> {isSubNodal ? "Vigilance Sub-Nodal" : "Vigilance Authority"}
            </span>
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2 px-3"
              onClick={() => navigate(-1)}
            >
              <i className="ti ti-arrow-left"></i> Back
            </button>
          </div>
        </div>

        {/* TABS HEADER CARD */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body p-2">
            <div className="nav nav-pills nav-fill gap-2" role="tablist">
              <button
                type="button"
                className={`nav-link py-2.5 px-4 fw-semibold d-flex align-items-center justify-content-center gap-2 ${
                  tab === "Pending" ? "active bg-primary text-white" : "text-muted bg-light"
                }`}
                style={{ borderRadius: 8, transition: "all 0.2s ease" }}
                onClick={() => {
                  setTab("Pending");
                  setSelected(null);
                }}
              >
                <i className="ti ti-clock"></i>
                Pending Cases
                <span className={`badge ms-1 ${tab === "Pending" ? "bg-white text-primary" : "bg-secondary text-white"}`}>
                  {pending.length}
                </span>
              </button>
              <button
                type="button"
                className={`nav-link py-2.5 px-4 fw-semibold d-flex align-items-center justify-content-center gap-2 ${
                  tab === "Processed" ? "active bg-primary text-white" : "text-muted bg-light"
                }`}
                style={{ borderRadius: 8, transition: "all 0.2s ease" }}
                onClick={() => {
                  setTab("Processed");
                  setSelected(null);
                }}
              >
                <i className="ti ti-circle-check"></i>
                Processed Cases
                <span className={`badge ms-1 ${tab === "Processed" ? "bg-white text-primary" : "bg-secondary text-white"}`}>
                  {processed.length}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* CASES LIST SECTION */}
        {loading ? (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <p className="text-muted mt-3 mb-0">Loading vigilance cases...</p>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body py-5">
              <EmptyState
                icon="ti-shield-check"
                message={
                  tab === "Pending"
                    ? "No pending vigilance cases on your desk."
                    : "No processed vigilance cases yet."
                }
              />
            </div>
          </div>
        ) : (
          <div className="row g-3 mb-4">
            {rows.map((c) => {
              const meta = statusMeta(c.status);
              const isSelected = selected?.id === c.id;
              const initials = (c.emp_name || "EM")
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase();

              return (
                <div className="col-md-6 col-xl-4" key={c.id}>
                  <div
                    className="card border h-100 position-relative transition-all"
                    style={{
                      borderRadius: 12,
                      cursor: "pointer",
                      borderColor: isSelected ? "#0d6efd" : "#e9ecef",
                      background: isSelected ? "#f0f7ff" : "#fff",
                      boxShadow: isSelected ? "0 4px 12px rgba(13,110,253,0.12)" : "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => openCase(c)}
                  >
                    <div className="card-body p-3.5 d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-2.5">
                        <div className="d-flex align-items-center gap-2.5 overflow-hidden">
                          <span
                            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                            style={{
                              width: 38,
                              height: 38,
                              background: isSelected ? "#0d6efd" : "#e9ecef",
                              color: isSelected ? "#fff" : "#495057",
                              fontSize: 13,
                            }}
                          >
                            {initials}
                          </span>
                          <div className="overflow-hidden">
                            <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: 14 }}>
                              {c.emp_name || c.emp_no || c.request_id}
                            </h6>
                            <small className="text-muted d-block text-truncate">
                              Emp No: <span className="fw-semibold text-dark">{c.emp_no || "—"}</span>
                            </small>
                          </div>
                        </div>
                        <span
                          className="badge flex-shrink-0"
                          style={{ background: meta.bg, color: meta.color, fontSize: 11, padding: "4px 8px" }}
                        >
                          {meta.label}
                        </span>
                      </div>

                      <div className="bg-light rounded-2 p-2 mb-2 small text-muted">
                        <div className="d-flex justify-content-between mb-1">
                          <span>Request ID:</span>
                          <span className="fw-semibold text-dark">{c.request_id}</span>
                        </div>
                        {c.dispatched_at && (
                          <div className="d-flex justify-content-between">
                            <span>Dispatched:</span>
                            <span className="fw-medium text-dark">{String(c.dispatched_at).slice(0, 10)}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-auto pt-2 border-top d-flex align-items-center justify-content-between small text-muted">
                        <span className="fw-medium text-primary">
                          {tab === "Pending" ? "Tap to review & clear" : "Tap to view details"}
                        </span>
                        <i className={`ti ${isSelected ? "ti-check text-primary" : "ti-chevron-right"}`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CASES WITH SUB-NODALS + SUB-NODAL REGISTRY — vigilance nodal only */}
        {isNodal && !loading && (
          <>
            {delegated.length > 0 && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark">
                    With Sub-Nodals ({delegated.length})
                  </h5>
                  <div className="text-muted small mt-1">
                    These left your desk and return once the sub-nodal submits their findings.
                  </div>
                </div>
                <div className="card-body p-4 d-flex flex-column gap-2">
                  {delegated.map((c) => (
                    <div
                      key={c.id}
                      className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 rounded-3 border bg-light"
                    >
                      <div>
                        <span className="fw-semibold text-dark">{c.emp_name || c.emp_no || c.request_id}</span>
                        <span className="text-muted small ms-2">
                          ({c.request_id}) →{" "}
                          <span className="fw-medium text-dark">
                            {c.delegated_to_name || c.delegated_to_emp_id}
                          </span>
                          {c.delegated_at ? ` · ${formatDateLong(c.delegated_at)}` : ""}
                        </span>
                      </div>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => doRevoke(c)}>
                        Revoke Delegation
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark">My Sub-Nodals</h5>
                <div className="text-muted small mt-1">
                  Add team members you can hand individual vigilance cases to. They record their findings
                  and you send the final outcome to HR.
                </div>
              </div>
              <div className="card-body p-4">
                <div className="row g-3 align-items-end mb-4">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Add a sub-nodal
                    </label>
                    <EmployeeSearchInput
                      value={newSubQuery}
                      onChange={(v) => {
                        setNewSubQuery(v);
                        setNewSubEmpId("");
                      }}
                      onSelect={(item) => {
                        setNewSubQuery(`${item.emp_no} — ${item.emp_name}`);
                        setNewSubEmpId(item.emp_no);
                      }}
                      placeholder="Search by employee number or name…"
                    />
                  </div>
                  <div className="col-md-4">
                    <button
                      className="btn btn-primary w-100"
                      style={{ height: 42 }}
                      onClick={addSubNodal}
                      disabled={savingSub || !newSubEmpId}
                    >
                      {savingSub ? (
                        <span className="spinner-border spinner-border-sm me-2" />
                      ) : (
                        <i className="ti ti-plus me-1" />
                      )}
                      Add Sub-Nodal
                    </button>
                  </div>
                </div>

                {subNodals.length === 0 ? (
                  <div className="text-center py-3 text-muted small">
                    <i className="ti ti-users fs-2 d-block mb-2 text-secondary" />
                    No sub-nodals registered yet.
                  </div>
                ) : (
                  <div className="row g-3">
                    {subNodals.map((x) => (
                      <div key={x.id} className="col-md-6 col-lg-4">
                        <div className="p-3 rounded-3 border bg-light d-flex justify-content-between align-items-center">
                          <div>
                            <div className="fw-semibold text-dark">
                              {x.sub_nodal_name || x.sub_nodal_emp_id}
                            </div>
                            <small className="text-muted d-block">{x.sub_nodal_emp_id}</small>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            title="Remove"
                            onClick={() => removeSubNodal(x)}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* SELECTED CASE DETAIL CARDS */}
        {selected && (
          <div className="mt-4">
            {/* SECTION 01: CASE DETAILS */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
              <div className="card-header bg-white border-bottom py-3 px-4">
                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                  <span
                    className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                    style={{ width: 34, height: 34, fontSize: 15 }}
                  >
                    01
                  </span>
                  Case Details
                </h5>
              </div>
              <div className="card-body p-4">
                <div className="row g-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee Name
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.emp_name || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Employee No.
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.emp_no || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Request ID
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.request_id || "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Sent On
                    </label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={selected.dispatched_at ? formatDateLong(selected.dispatched_at) : "—"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Status
                    </label>
                    <div className="form-control bg-light d-flex align-items-center">
                      <StatusBadge status={selected.status} />
                    </div>
                  </div>

                  {selected.delegated_to_emp_id && (
                    <>
                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Delegated To
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={`${selected.delegated_to_name || "—"} (${selected.delegated_to_emp_id})`}
                          readOnly
                        />
                      </div>
                      {selected.delegated_at && (
                        <div className="col-lg-3 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Delegated On
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={formatDateLong(selected.delegated_at)}
                            readOnly
                          />
                        </div>
                      )}
                    </>
                  )}
                  {tab === "Processed" && (
                    <>
                      {selected.acted_at && (
                        <div className="col-lg-3 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Actioned On
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={formatDateLong(selected.acted_at)}
                            readOnly
                          />
                        </div>
                      )}
                      {selected.pending_case_ref && (
                        <div className="col-lg-3 col-md-4 col-sm-6">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Pending Case Ref.
                          </label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={selected.pending_case_ref}
                            readOnly
                          />
                        </div>
                      )}
                      {selected.remarks && (
                        <div className="col-12">
                          <label className="form-label fw-semibold text-secondary small mb-1">
                            Remarks
                          </label>
                          <textarea
                            className="form-control bg-light"
                            rows={2}
                            value={selected.remarks}
                            readOnly
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* SUB-NODAL FINDINGS — what the sub-nodal recorded; the nodal reviews it and
                sends the final outcome to HR. */}
            {selected.sub_nodal_acted_at && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
                <div className="card-header bg-white border-bottom py-3 px-4 d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      <i className="ti ti-user-check" />
                    </span>
                    Sub-Nodal's Findings
                  </h5>
                  {selected.sub_nodal_status && <StatusBadge status={selected.sub_nodal_status} />}
                </div>
                <div className="card-body p-4">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Recorded By
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={`${selected.delegated_to_name || "—"} (${selected.delegated_to_emp_id || "—"})`}
                        readOnly
                      />
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Findings
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={decisionLabel(selected.sub_nodal_status)}
                        readOnly
                      />
                    </div>
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="form-label fw-semibold text-secondary small mb-1">
                        Submitted On
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formatDateLong(selected.sub_nodal_acted_at)}
                        readOnly
                      />
                    </div>
                    {selected.sub_nodal_pending_case_ref && (
                      <div className="col-lg-3 col-md-4 col-sm-6">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Pending Case Ref.
                        </label>
                        <input
                          type="text"
                          className="form-control bg-light"
                          value={selected.sub_nodal_pending_case_ref}
                          readOnly
                        />
                      </div>
                    )}
                    {selected.sub_nodal_remarks && (
                      <div className="col-12">
                        <label className="form-label fw-semibold text-secondary small mb-1">
                          Remarks
                        </label>
                        <textarea
                          className="form-control bg-light"
                          rows={2}
                          value={selected.sub_nodal_remarks}
                          readOnly
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* DELEGATE TO SUB-NODAL — only the nodal, only on a case still on their own desk */}
            {tab === "Pending" && isNodal && selected.status === "PENDING" && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      <i className="ti ti-arrow-forward-up" />
                    </span>
                    Delegate to Sub-Nodal
                  </h5>
                </div>
                <div className="card-body p-4">
                  {subNodals.length === 0 ? (
                    <p className="text-muted small mb-0">
                      You have no sub-nodals yet. Add one in "My Sub-Nodals" below to hand cases off, or
                      record the clearance yourself.
                    </p>
                  ) : (
                    <>
                      <p className="text-muted small">
                        Hand this case to one of your sub-nodals. They record their findings and it
                        comes back to you, and you then send the final outcome to HR.
                      </p>
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <select
                          className="form-select"
                          style={{ maxWidth: 340 }}
                          value={delegateTo}
                          onChange={(e) => setDelegateTo(e.target.value)}
                        >
                          {subNodals.map((x) => (
                            <option key={x.id} value={x.sub_nodal_emp_id}>
                              {x.sub_nodal_name || x.sub_nodal_emp_id} ({x.sub_nodal_emp_id})
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={doDelegate}
                          disabled={delegating || !delegateTo}
                        >
                          {delegating ? (
                            <span className="spinner-border spinner-border-sm me-1" />
                          ) : (
                            <i className="ti ti-arrow-forward-up me-1" />
                          )}
                          Delegate
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* SECTION 02: CLEARANCE DECISION (FOR PENDING CASES) */}
            {tab === "Pending" && (isNodal || selected.status === "DELEGATED") && (
              <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: 12 }}>
                <div className="card-header bg-white border-bottom py-3 px-4">
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <span
                      className="d-inline-flex align-items-center justify-content-center fw-bold bg-primary text-white rounded-3 flex-shrink-0"
                      style={{ width: 34, height: 34, fontSize: 15 }}
                    >
                      02
                    </span>
                    {isSubNodal ? "Your Findings" : "Clearance Decision"}
                  </h5>
                </div>
                <div className="card-body p-4">
                  {/* Status selection cards */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold text-secondary small mb-2">
                      Clearance Status <span className="text-danger">*</span>
                    </label>
                    <div className="row g-3">
                      {DECISION_OPTIONS.map((opt) => {
                        const isChosen = status === opt.key;
                        return (
                          <div className="col-md-4" key={opt.key}>
                            <button
                              type="button"
                              className="btn w-100 text-start p-3.5 h-100 position-relative"
                              style={{
                                borderRadius: 10,
                                border: `2px solid ${isChosen ? opt.color : "#e9ecef"}`,
                                background: isChosen ? `${opt.color}0D` : "#fff",
                                transition: "all 0.2s ease-in-out",
                              }}
                              onClick={() => setStatus(opt.key)}
                            >
                              <div className="d-flex align-items-center justify-content-between mb-2">
                                <span
                                  className="d-inline-flex align-items-center justify-content-center rounded-3 p-2"
                                  style={{
                                    background: isChosen ? opt.color : "#f8f9fa",
                                    color: isChosen ? "#fff" : opt.color,
                                  }}
                                >
                                  <i className={`ti ${opt.icon} fs-5`} />
                                </span>
                                {isChosen && (
                                  <span
                                    className="badge rounded-circle p-1"
                                    style={{ background: opt.color, color: "#fff" }}
                                  >
                                    <i className="ti ti-check" />
                                  </span>
                                )}
                              </div>
                              <div
                                className="fw-bold"
                                style={{ color: isChosen ? opt.color : "#212529", fontSize: 14 }}
                              >
                                {opt.label}
                              </div>
                              <small className="text-muted d-block mt-1" style={{ fontSize: 11.5 }}>
                                {opt.desc}
                              </small>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Remarks {(status === "HOLD" || status === "OBJECTION") && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Enter remarks explaining clearance decision / details..."
                    />
                  </div>

                  {/* Pending case reference */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      Pending Case Reference <span className="text-muted fw-normal">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={pendingCaseRef}
                      onChange={(e) => setPendingCaseRef(e.target.value)}
                      placeholder="e.g. VIG/2026/00123"
                    />
                  </div>
                </div>

                <div className="card-footer bg-white border-top p-3 d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-outline-secondary px-4" onClick={closeCase}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4 d-inline-flex align-items-center gap-2"
                    onClick={handleSubmit}
                    disabled={!status || submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-send" />
                        {isSubNodal ? "Submit to Nodal" : "Send to HR"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {tab === "Processed" && (
              <div className="d-flex justify-content-end mb-4">
                <button type="button" className="btn btn-outline-secondary px-4" onClick={closeCase}>
                  Close Details
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VigilanceClearancePage;
